/**
 * Export editor state to TMJ (Tiled Map JSON) format.
 * Supports tile animations and per-tile collision shapes.
 */
export function exportTMJ(state) {
  const mapWidth = state.get('mapWidth');
  const mapHeight = state.get('mapHeight');
  const tileWidth = state.get('tileWidth');
  const tileHeight = state.get('tileHeight');
  const layers = state.get('layers');
  const tilesets = state.get('tilesets');
  const properties = state.get('properties');
  const tileAnimations = state.get('tileAnimations');
  const tileCollisions = state.get('tileCollisions');

  const tmj = {
    compressionlevel: -1,
    height: mapHeight,
    infinite: false,
    layers: layers.map((layer, i) => exportLayer(layer, mapWidth, mapHeight, i + 1)),
    nextlayerid: layers.length + 1,
    nextobjectid: getNextObjectId(layers),
    orientation: state.get('orientation') || 'orthogonal',
    renderorder: state.get('renderOrder') || 'right-down',
    tiledversion: '1.11.0',
    tileheight: tileHeight,
    tilesets: tilesets.map(ts => {
      const exported = {
        columns: ts.columns,
        firstgid: ts.firstGid,
        image: normalizeTilesetPath(ts),
        imageheight: ts.imageHeight,
        imagewidth: ts.imageWidth,
        margin: ts.margin || 0,
        name: ts.name,
        spacing: ts.spacing || 0,
        tilecount: ts.tileCount,
        tileheight: ts.tileHeight,
        tilewidth: ts.tileWidth,
      };

      // Collect per-tile data (animations and collision shapes)
      const tilesData = collectTilesData(ts, tileAnimations, tileCollisions);
      if (tilesData.length > 0) {
        exported.tiles = tilesData;
      }

      return exported;
    }),
    tilewidth: tileWidth,
    type: 'map',
    version: '1.10',
    width: mapWidth,
  };

  if (properties && Object.keys(properties).length > 0) {
    tmj.properties = exportProperties(properties);
  }

  return tmj;
}

function collectTilesData(ts, tileAnimations, tileCollisions) {
  const tiles = [];
  const localIds = new Set();

  // Find all local tile IDs that have data
  if (tileAnimations) {
    for (const [globalId] of tileAnimations) {
      if (globalId >= ts.firstGid && globalId < ts.firstGid + ts.tileCount) {
        localIds.add(globalId - ts.firstGid);
      }
    }
  }
  if (tileCollisions) {
    for (const [globalId] of tileCollisions) {
      if (globalId >= ts.firstGid && globalId < ts.firstGid + ts.tileCount) {
        localIds.add(globalId - ts.firstGid);
      }
    }
  }

  for (const localId of [...localIds].sort((a, b) => a - b)) {
    const globalId = ts.firstGid + localId;
    const entry = { id: localId };

    // Animation
    if (tileAnimations && tileAnimations.has(globalId)) {
      entry.animation = tileAnimations.get(globalId).map(f => ({
        tileid: f.tileId,
        duration: f.duration,
      }));
    }

    // Collision shapes → objectgroup
    if (tileCollisions && tileCollisions.has(globalId)) {
      const shapes = tileCollisions.get(globalId);
      let objId = 1;
      entry.objectgroup = {
        draworder: 'index',
        id: 2,
        name: '',
        objects: shapes.map(shape => {
          if (shape.type === 'rect') {
            return {
              id: objId++,
              x: shape.x,
              y: shape.y,
              width: shape.width,
              height: shape.height,
            };
          } else if (shape.type === 'polygon') {
            return {
              id: objId++,
              x: shape.x || 0,
              y: shape.y || 0,
              polygon: shape.points.map(p => ({ x: p.x, y: p.y })),
            };
          }
          return { id: objId++, x: 0, y: 0 };
        }),
        opacity: 1,
        type: 'objectgroup',
        visible: true,
        x: 0,
        y: 0,
      };
    }

    tiles.push(entry);
  }

  return tiles;
}

function exportLayer(layer, mapWidth, mapHeight, id) {
  if (layer.type === 'objectgroup') {
    return {
      draworder: 'topdown',
      id,
      name: layer.name,
      objects: (layer.objects || []).map(obj => {
        const exported = {
          id: obj.id,
          name: obj.name || '',
          type: obj.type || '',
          x: obj.x,
          y: obj.y,
          width: obj.width || 0,
          height: obj.height || 0,
          rotation: obj.rotation || 0,
          visible: obj.visible !== false,
        };
        if (obj.polygon) exported.polygon = obj.polygon;
        if (obj.polyline) exported.polyline = obj.polyline;
        if (obj.point) exported.point = true;
        if (obj.gid) exported.gid = obj.gid;
        if (obj.properties && Object.keys(obj.properties).length > 0) {
          exported.properties = exportProperties(obj.properties);
        }
        return exported;
      }),
      opacity: layer.opacity ?? 1,
      type: 'objectgroup',
      visible: layer.visible !== false,
      x: 0,
      y: 0,
    };
  }

  const tileLayer = {
    data: Array.from(layer.data || new Uint32Array(mapWidth * mapHeight)),
    height: mapHeight,
    id,
    name: layer.name,
    opacity: layer.opacity ?? 1,
    type: 'tilelayer',
    visible: layer.visible !== false,
    width: mapWidth,
    x: layer.offsetX || 0,
    y: layer.offsetY || 0,
  };

  if (layer.properties && Object.keys(layer.properties).length > 0) {
    tileLayer.properties = exportProperties(layer.properties);
  }

  return tileLayer;
}

function exportProperties(props) {
  if (!props || Object.keys(props).length === 0) return undefined;
  return Object.entries(props).map(([name, val]) => {
    if (typeof val === 'object' && val.type) {
      return { name, type: val.type, value: val.value };
    }
    return { name, type: typeof val === 'boolean' ? 'bool' : typeof val === 'number' ? 'float' : 'string', value: val };
  });
}

/**
 * Normalize tileset image paths for export.
 * IDB library paths → friendly filename. Blob URLs → warning filename.
 * Relative/absolute paths → pass through unchanged.
 */
function normalizeTilesetPath(ts) {
  const path = ts.image || '';

  // IDB library paths: "tilesets/kenney-tiny-town/image.png" → "Tiny Town (Kenney).png"
  if (path.startsWith('tilesets/') && path.endsWith('/image.png')) {
    const safeName = (ts.name || 'tileset').replace(/[<>:"/\\|?*]/g, '_');
    return `${safeName}.png`;
  }

  // Blob URLs are session-only — export a resolvable name so the file can be reopened
  if (path.startsWith('blob:')) {
    const safeName = (ts.name || 'tileset').replace(/[<>:"/\\|?*]/g, '_');
    return `${safeName}.png`;
  }

  // Normal relative or absolute path — pass through
  return path;
}

/**
 * Export as a Blob using chunked serialization.
 * For large maps, avoids creating a single multi-MB string by writing
 * layer data arrays in chunks. Returns a Blob that can be downloaded
 * or saved directly.
 *
 * @param {Object} state — EditorState
 * @returns {Blob}
 */
export function exportTMJBlob(state) {
  const tmj = exportTMJ(state);
  const CHUNK_SIZE = 50000; // Elements per chunk for layer data arrays

  // Check if any layer is large enough to benefit from chunking
  const hasLargeLayer = tmj.layers.some(l => l.data && l.data.length > CHUNK_SIZE);

  if (!hasLargeLayer) {
    // Small map — standard stringify is fine
    return new Blob([JSON.stringify(tmj, null, 2)], { type: 'application/json' });
  }

  // Large map — build JSON in chunks to avoid one giant string allocation
  const parts = [];

  // Clone tmj without layers (we'll serialize layers manually)
  const header = { ...tmj, layers: undefined };
  let headerJson = JSON.stringify(header, null, 2);
  // Remove trailing "}" and the undefined layers field
  headerJson = headerJson.replace(/,?\s*"layers":\s*(?:null|undefined)\s*/, '');
  // Remove closing brace — we'll add layers and close manually
  headerJson = headerJson.replace(/\}\s*$/, '');
  parts.push(headerJson);
  parts.push(',\n  "layers": [\n');

  for (let li = 0; li < tmj.layers.length; li++) {
    const layer = tmj.layers[li];

    if (layer.data && layer.data.length > CHUNK_SIZE) {
      // Serialize layer without data, then stream data separately
      const layerCopy = { ...layer, data: undefined };
      let layerJson = JSON.stringify(layerCopy, null, 4);
      // Remove the undefined data field and closing brace
      layerJson = layerJson.replace(/,?\s*"data":\s*(?:null|undefined)\s*/, '');
      layerJson = layerJson.replace(/\}\s*$/, '');
      parts.push('    ');
      parts.push(layerJson);
      parts.push(',\n      "data": [');

      // Stream data in chunks
      const data = layer.data;
      for (let i = 0; i < data.length; i += CHUNK_SIZE) {
        const chunk = data.slice(i, Math.min(i + CHUNK_SIZE, data.length));
        if (i > 0) parts.push(',');
        parts.push(chunk.join(','));
      }

      parts.push(']\n    }');
    } else {
      parts.push('    ');
      parts.push(JSON.stringify(layer, null, 4));
    }

    if (li < tmj.layers.length - 1) parts.push(',\n');
  }

  parts.push('\n  ]\n}');

  return new Blob(parts, { type: 'application/json' });
}

function getNextObjectId(layers) {
  let max = 0;
  for (const layer of layers) {
    if (layer.type === 'objectgroup' && layer.objects) {
      for (const obj of layer.objects) {
        if (obj.id > max) max = obj.id;
      }
    }
  }
  return max + 1;
}
